"use client"
import Image from "next/image"
import Button from "./button/Button"
import { RecursiveTranslations } from "@/data/types"
import { h1Style, h5Style } from "@/data/styles"
import clsx from "clsx"
import { useState } from "react"
import { useRouter } from "next/navigation"
import { ArrowRightIcon } from "../icons"
import Dropdown from "./Dropdown"
import { getCityOptions, getPropertyTypeOptions } from "@/utils/helpers"

type Props = {
  translations?: RecursiveTranslations;
  imgUrl: string;
  isButtonPresent?: boolean;
  isFilterPresent?: boolean;
  layout?: "center" | "bottom";
  title?: string;
  subtitle?: string | React.ReactNode;
  height?: string;       
  bottomPadding?: string;
  availableCities?: string[];
  availablePropertyTypes?: string[];
  className?: string
  narrowText?: boolean
};

export default function Hero({ translations, imgUrl, isButtonPresent = false, isFilterPresent = false, layout = "center",
  title, subtitle, height = "h-screen", bottomPadding, availableCities = [], availablePropertyTypes = [], className, narrowText = false
}: Props) {

  const displayTitle = title ?? translations?.title
  const router = useRouter()

  const [city, setCity] = useState("")
  const [propertyType, setPropertyType] = useState("")

  const handleSearch = () => {
    const query = new URLSearchParams()
    if (city) query.append("city", city)
    if (propertyType) query.append("propertyType", propertyType)

    router.push(`/projects?${query.toString()}`)
  }

  const paddingClass = bottomPadding
    ? bottomPadding
    : layout === "center"
    ? "pb-[80px]"
    : "pb-[60px] lg:pb-[90px]"

  const containerClasses = clsx(
    "absolute inset-0 flex flex-col max-w-[1440px] mx-auto px-[20px] md:px-[50px]",
    layout === "center" ? "justify-center" : "justify-end",
    paddingClass
  )

  const cityOptionsFormatted = getCityOptions(availableCities, translations as RecursiveTranslations)
  const propertyTypeOptionsFormatted = getPropertyTypeOptions(availablePropertyTypes, translations as RecursiveTranslations)

  return (
    <section className={clsx("relative bg-[#1B1D2B]", height, className)}>
      <Image src={imgUrl} alt="Hero background" fill className="object-cover" priority />
      <div className="absolute inset-0 bg-gradient-to-t from-[#101E2C]/90 to-[#101E2C]/60"></div>

      <div className={containerClasses}>
        <div className={clsx("text-white mt-[130px]", narrowText && "max-w-[740px]")}>
          <h1 className={h1Style}>{displayTitle as string}</h1>

          {subtitle ? (
            typeof subtitle === "string" ? (
              <h5 className={clsx(h5Style, "mt-[15px]")}>{subtitle}</h5>
            ) : (
              <div className="mt-[15px] text-[35px] leading-[45px]">{subtitle}</div>
            )
          ) : (
            <h5 className={clsx(h5Style, "mt-[15px]")}>{translations?.subtitle as string}</h5>
          )}
        </div>

        {isFilterPresent && (
          <div className="mt-[30px] flex flex-col lg:flex-row gap-[15px]">
            {/* City */}
            <div className="bg-[white]/10 backdrop-blur-sm flex flex-col lg:flex-row rounded-[10px] px-[20px] py-[7px] gap-[15px]">
              <Dropdown
                options={cityOptionsFormatted}
                value={city}
                onChange={(value) => {
                  if (typeof value === "string") {
                    setCity(value)
                  }
                }}
                label={"Area, project or community"}
                className="min-w-[160px] max-w-full text-white"
                valueContainerColor="white"
              />
              <div className="w-full h-px bg-white/60 lg:w-px lg:h-auto my-1 lg:my-0" />

              <Dropdown
                options={propertyTypeOptionsFormatted}
                value={propertyType}
                onChange={(value) => {
                  if (typeof value === "string") {
                    setPropertyType(value)
                  }
                }}
                label={translations?.propertyTypeLabel as string}
                className="min-w-[160px] max-w-full text-white"
                valueContainerColor="white"
              />
            </div>
            


            {/* Search Button */}
            <Button onClick={handleSearch} rightIcon={<ArrowRightIcon color="white" width={22} height={22} />}>
              {translations?.buttonLabel as string ?? "Search"}
            </Button>
          </div>
        )}

        {isButtonPresent && (
          <div className="mt-[20px]">
            <Button>{translations?.cta as string ?? "Explore More"}</Button>
          </div>
        )}
      </div>
    </section>
  )
}
